import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { Candidate } from '../models';
import { ApiService } from './api.service';
import { Pagination } from '@shared/pagination';
import { TalentPoolPinned } from '@shared/models/talent-pool';
import { TalentPoolsCandidates } from '@shared/models/talent-pools-candidates';
import { TalentPoolTag, TalentPoolTagAdapter } from 'mojo-talent-pools';
import { map } from 'rxjs/operators';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { MojoPrivilegesService } from 'mojo-privileges';
import { appConfig } from '@app/app.config';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import {
  CandidatesOnEverySingleStatus,
  MojoTalentPoolsService,
  TalentPool,
} from 'mojo-talent-pools';

@Injectable()
export class TalentPoolsService extends ApiService<TalentPool> implements MojoTalentPoolsService {
  constructor(
    http: HttpClient,
    private featureExtensionService: MojoFeatureExtensionsService,
    private privilegesService: MojoPrivilegesService,
  ) {
    super(http);
    this.url = 'talent-pools';
  }

  talentPoolsPrivilege = appConfig.privileges.talentPools.view;
  talentPoolsFe = AppFeatureExtensions.TALENT_POOLS;

  createTalentPool(body: TalentPool): Observable<TalentPool> {
    return this.post({ url: '', body });
  }
  updateTalentPool(id: number, body: TalentPool): Observable<TalentPool> {
    return this.patch({ url: `/${id}`, body });
  }

  addCandidatesToTalentPools(body: TalentPoolsCandidates): Observable<TalentPool[]> {
    return this.post({ url: '/candidates', body });
  }
  autocomplete(
    talentPoolName: string,
    size = 20,
    instantSearch?,
    hasTalentPoolFeatureExtension = true,
  ): Observable<TalentPoolTag[]> {
    return !!hasTalentPoolFeatureExtension
      ? this.get({
          url: '/autocomplete',
          params: { talentPoolName, size, instantSearch: !!instantSearch },
        }).pipe(
          map((talentPools: TalentPool[]) =>
            talentPools.map((pool) => TalentPoolTagAdapter.toTag(pool)),
          ),
        )
      : of([]);
  }

  hasTalentPoolPrivilegeAndFe() {
    return combineLatest([
      this.privilegesService.hasPrivilege(this.talentPoolsPrivilege),
      this.featureExtensionService.hasFeatureExtension(this.talentPoolsFe),
    ]).pipe(map(([privilege, fe]) => privilege && fe));
  }

  pinOrUnpin(payload: TalentPoolPinned[]): Observable<TalentPool> {
    return this.post({ url: '/pin', body: payload });
  }

  //MojoTalentPoolsService interface implementation
  getTalentPools(page: number): Observable<Pagination<TalentPool>> {
    return this.get({ url: '', params: { page } });
  }

  talentPoolWithDetails(id: number): Observable<TalentPool> {
    return this.get({ url: `/${id}` });
  }

  getTotalTalentsOnEverySingleStatus(
    talentPoolId: number,
    filters: any,
  ): Observable<CandidatesOnEverySingleStatus> {
    return this.post<CandidatesOnEverySingleStatus>({
      url: `/candidates/count-by-status?talentPoolId=${talentPoolId}&external=false`,
      body: {},
    });
  }

  talentPoolCandidates(page: number, id: number, filters?: any): Observable<Pagination<Candidate>> {
    return this.post({ url: `/${id}/candidates/search?page=${page}`, body: filters });
  }

  changeTalentPoolCandidateStatus(
    talentPoolId: number,
    candidateId: number,
    body: { statusName: string },
  ) {
    return this.patch({ url: `/${talentPoolId}/candidates/${candidateId}?assign-status`, body });
  }

  removeCandidatesFromTalentPool(talentPoolId: number, candidateIds: any[]) {
    return this.patch({
      url: `/${talentPoolId}/candidates?unassign`,
      body: candidateIds,
    });
  }

  getTalentPoolsForCandidate(id: number, page: number): Observable<TalentPool[]> {
    return this.get({ url: `/candidate/${id}`, params: { page } });
  }

  deleteTalentPool(id: number) {
    return this.delete({ url: `/${id}` });
  }
}
